import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect } from "react";
import { BusinessUpgrade } from "@/features/business/BusinessUpgrade";
import { BusinessEntitlementProvider, useBusinessEntitlement } from "@/lib/entitlement";
import { followPostLoginContinuation } from "@/lib/return-to";
import { useSession } from "@/lib/session";

function LoadingScreen() {
  return (
    <div className="grid min-h-screen place-items-center text-sm text-[var(--muted-foreground)]">
      Loading...
    </div>
  );
}

/** Renders `children` once signed in; otherwise redirects to `/login`. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (state.status === "anonymous") void navigate({ to: "/login" });
  }, [state.status, navigate]);

  if (state.status !== "authenticated") return <LoadingScreen />;
  return <>{children}</>;
}

/**
 * Renders `children` only for entitled Business content. The upgrade
 * screen stands in otherwise; the gate fails closed while the entitlement
 * loads or a read errors.
 */
export function RequireBusiness({ children, organizationId }: { children: ReactNode; organizationId?: string }) {
  return <BusinessEntitlementProvider organizationId={organizationId}><BusinessRoute>{children}</BusinessRoute></BusinessEntitlementProvider>;
}

function BusinessRoute({ children }: { children: ReactNode }) {
  const entitlement = useBusinessEntitlement();
  if (entitlement.loading) return <LoadingScreen />;
  if (!entitlement.business) return <BusinessUpgrade />;
  return <>{children}</>;
}

/**
 * Renders `children` only when signed out. A signed-in visitor goes back to the
 * partner app that sent them (allowed `return_to`), otherwise to `/home`.
 */
export function RequireAnonymous({ children }: { children: ReactNode }) {
  const { state } = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (state.status !== "authenticated") return;
    if (!followPostLoginContinuation(window.location.search)) void navigate({ to: "/home" });
  }, [state.status, navigate]);

  if (state.status === "loading") return <LoadingScreen />;
  if (state.status === "authenticated") return <LoadingScreen />;
  return <>{children}</>;
}
